/* eslint-disable max-statements */
/* eslint-disable max-lines */
import { AcwCopilotUtility } from "@blk/acw-global-settings";
import { createReducer } from "@reduxjs/toolkit";
import { isEmpty, xor } from "lodash";
import { generate } from "short-uuid";
import { applyOriginalWidgetPipelineParameterVisitorsIntoInspectDataOne, isActionType, isAddWidgetCloseModalAction, isAssetLocationChangeAction, isCashFlowLocationChangeAction, isCashflowModelAssociationChangeReduxQueryAction, isDatapointsInvalidationAction, isDatapointsInvalidationAfterLocationChangedAction, isDeletePortfoliosReduxQueryAction, isPutModelAssociationReduxQueryAction, isFundLocationChangeAction, isFundSeriesLocationChangeAction, isMainGridLocationChangeAction, isManagerLocationChangeAction, isMarketLocationChangeAction, isMarketMutationAction, isModifyBenchmarkAllocationAction, isPortfolioLocationChangeAction, isPrivateBenchmarkLocationChangeAction, isPublicEquityIndicesLocationChangeAction, isResetBenchmarkCreation, isTaUndoRedoAction, resetDatapointsCache, syncPreviewGridColumns, updateGridDimensions, updateGridUserSettings } from "./utils";
import { DatapointsEntityType } from "api/ux/src";
import { InsBenchmarkCreationBlendedGridId, inspectData, preview } from "common/constants";
import { INVESTOR_ID } from "common/constants/routes";
import { capitalizeFirstLetter } from "common/string-helper";
import { setForceScrollToWidgetId } from "components";
import { InspectDataComparisonTab, InspectDataTab } from "components/modal/widget-inspect-data/types";
import { WidgetType } from "components/modal/widget-workshop/types";
import { privateEquityIndicesCustomGrid, preqinBenchmarkStandardGrid, insightResearchStandardGrid } from "components/pages/administration/private-equity-benchmark/insPrivateEquityBenchmarks";
import { publicEquityIndicesGridState } from "components/pages/administration/public-equity-indices/insPublicEquityIndicesContent";
import { DisplayAs } from "components/ux/ins-date-pickers/types";
import { analyticsCopilotWidgetsSectionId } from "components/widgets/base/constants";
import { analysisSelector, pathInformationSelector } from "components/workspaces/hooks/use-current-dashboard";
import { analysesListFetched, analysisUpdated, pageFetched } from "components/workspaces/store/api";
import { workspaceEntityTypeDataToWorkspaceEntityTypeMap } from "components/workspaces/store/types";
import { modelsAssociationsQueryKey } from "hooks/administration/use-get-funds-cashflow-models";
import { resetCashflowForecastingState } from "store/administration/cashflow-forecasting";
import { newWidgetReceived } from "store/analytics-copilot";
import { assistantChartPrefix } from "store/analytics-copilot/helper";
import { isTenorPreviewUpdatedAction, resetWorkspaceSettings, setCurrentStatusSectionWidget } from "store/current-dashboard";
import { DataScale } from "store/current-dashboard/types";
import { getDefaultValues, initSectionStatus, isDashboardChangedAction, isFundResponseReduxQueryAction, isLocationChangeAction } from "store/current-dashboard/utils";
import { initialState } from "store/grid-settings";
import { createInspectDataWidget, removeInspectDataWidget, setInspectDataSelectedTab } from "store/inspectdata";
import { datapointsKey } from "store/middleware/analytics/analytics-grouping-middleware";
import { addWidgetToSection, getDefaultTemplate, setSelectedWidget } from "store/template";
import { addColumn, addSettingsGroupingOptions, addSettingsGroupingOptionsDatapoints, applyGroupingOnColumns, isUpdatePreviewWidgetAction, isUpdateWidgetWorkshopColumnsAction, removeColumns, removeSettingsGroupingOptionsDatapoints, updateColumns } from "store/widget-workshop";
import { lazySectionLoaded, lazySectionWithWidgetsLoaded, lazyWidgetsLoaded } from "store/template/lazy-load";
export const sectionSelector = (sectionId) => (state) => state.designer.present?.sections?.[sectionId] ?? state.entities.Section[sectionId];
const getWidgetsArray = (state, widgetIds) => {
    const widgets = [];
    for (const id of widgetIds) {
        const designed = state.designer.present?.widgets?.[id];
        if (designed) {
            widgets.push(designed);
        }
        else {
            const widget = state.entities.Widget[id];
            if (widget) {
                const { parentId } = widget;
                if (parentId && state.entities.Widget[parentId]) {
                    widgets.push({ ...state.entities.Widget[parentId], id });
                }
                else {
                    widgets.push(widget);
                }
            }
        }
    }
    return widgets;
};
const shouldReinitializeSection = (state, currentSection, newSectionWidgetIds) => {
    if (!currentSection?.id) {
        return false;
    }
    const existingWidgets = state.currentDashboard.currentStatus.sections?.[currentSection.id]?.widgets;
    if (!existingWidgets) {
        return true;
    }
    const currentStatusWidgetIds = Object.keys(existingWidgets);
    return newSectionWidgetIds.length !== currentStatusWidgetIds.length ||
        xor(newSectionWidgetIds, currentStatusWidgetIds).length !== 0;
};
const applyWorkspaceSettingsToSection = (state, sectionId) => {
    const committed = state.currentDashboard.workspaceSettings;
    if (!committed) {
        return;
    }
    const sectionWidgets = state.currentDashboard.currentStatus.sections?.[sectionId]?.widgets;
    if (!sectionWidgets) {
        return;
    }
    Object.values(sectionWidgets).forEach((widget) => {
        if (!widget) {
            return;
        }
        widget.isWidgetLevelTimePeriod = false;
        if (committed.dateSettings) {
            widget.dateSettingsDefaultValues = { ...widget.dateSettingsDefaultValues, ...committed.dateSettings };
        }
        if (committed.dataGranularity) {
            widget.dataGranularity = committed.dataGranularity;
        }
    });
};
const populateSectionStatus = (state, currentSection) => {
    const widgetList = Object.values(currentSection?.widgets || {}).reduce((prev, curr) => prev.concat(curr.map((x) => x.id || "")), []), widgets = getWidgetsArray(state, widgetList);
    if (!shouldReinitializeSection(state, currentSection, widgetList)) {
        return;
    }
    const newSection = { section: currentSection, widgets };
    state.currentDashboard.currentStatus = {
        sections: { ...state.currentDashboard.currentStatus.sections, [currentSection.id || ""]: initSectionStatus(newSection, state.currentDashboard.referenceDate) }
    };
    applyWorkspaceSettingsToSection(state, currentSection.id || "");
};
const widgetAndChildrenArraySelector = (widgetId) => (state) => {
    const widgets = getWidgetsArray(state, [widgetId]);
    const widget = widgets[widgets.length - 1];
    if (widget) {
        const widgetChildrenId = widget.componentProps?.childrenId, widgetGroupedWidget = widget.componentProps?.widgets;
        if (widgetGroupedWidget?.length) {
            widgets.push(...getWidgetsArray(state, widgetGroupedWidget));
        }
        if (widgetChildrenId) {
            widgets.push(...getWidgetsArray(state, [widgetChildrenId]));
        }
    }
    return widgets;
};
const findAnalysisIndex = (state, currentSection) => {
    const pathInfo = pathInformationSelector(state);
    let index = 0;
    const currentTemplate = analysisSelector(pathInfo.selectedType)(state);
    for (const analysis of currentTemplate.analysis ?? []) {
        if (analysis.sections?.some((s) => s.id === currentSection)) {
            break;
        }
        index += 1;
    }
    return index;
};
const mapLayouts = (widget) => {
    const layouts = {};
    if (widget.layouts?.lg) {
        layouts.lg = widget.layouts.lg;
    }
    if (widget.layouts?.md) {
        layouts.md = widget.layouts.md;
    }
    if (widget.layouts?.sm) {
        layouts.sm = widget.layouts.sm;
    }
    if (widget.layouts?.xs) {
        layouts.xs = widget.layouts.xs;
    }
    return {
        id: widget.id,
        layouts
    };
};
const updateDesignerSectionsFromTemplate = (state, selectedType) => {
    const panelIndex = state?.designer?.present?.index ?? 0;
    const template = state.entities.Template[selectedType];
    const newState = template.analysis?.[panelIndex]?.sections?.reduce((prev, curr) => {
        if (curr.id) {
            prev[curr.id] = state?.designer?.present?.sections?.[curr.id] ?? state.entities.Section[curr.id];
        }
        return prev;
    }, {});
    state.designer.present.sections = { ...state.designer.present.sections, ...newState };
};
const syncDashboardWithSection = (state) => {
    const { selectedSection: requestedSection } = pathInformationSelector(state);
    state.currentDashboard.currentAnalysis = findAnalysisIndex(state, requestedSection);
    state.currentDashboard.currentSection = requestedSection;
    const currentSection = state.designer.present?.sections?.[requestedSection] || state.entities.Section?.[requestedSection];
    populateSectionStatus(state, currentSection);
};
/* eslint-disable max-lines-per-function */
export const crossSliceReducer = createReducer({}, (builder) => builder
    .addMatcher(isDatapointsInvalidationAfterLocationChangedAction, (state, action) => {
    if (state.grids.temporaryKey !== action.payload.location?.key) {
        state.grids.temporaryKey = action.payload.location?.key || "";
        state.entities.Datapoints = {};
        state.entities.DatapointsFilters = {};
        const keys = Object.keys(state.customQueries).filter((k) => k.includes(datapointsKey) || k.includes("PostFiltersForDatapoints"));
        for (const key of keys) {
            delete state.customQueries[key];
        }
    }
})
    .addMatcher(isDatapointsInvalidationAction, (state) => {
    resetDatapointsCache(state);
})
    .addMatcher(isMarketMutationAction, (state) => {
    state.entities.PrivateBenchmarks = {};
    const keys = Object.keys(state.customQueries).filter((k) => k.includes("GetPrivateBenchmarks"));
    for (const key of keys) {
        delete state.customQueries[key];
    }
})
    .addMatcher(isPortfolioLocationChangeAction, (state, action) => {
    const [, requestedId] = action.payload.location.pathname.split("/").filter((e) => e);
    if (!state.currentDashboard.isManualChangeDate) {
        state.datepicker.displayAs = DisplayAs.Date;
        if (state.entities.Portfolio[requestedId]?.latestInvestorCfDate ||
            state.entities.Portfolio[requestedId]?.referenceDate) {
            state.currentDashboard.referenceDate = state.entities.Portfolio[requestedId]?.latestInvestorCfDate ||
                state.entities.Portfolio[requestedId]?.referenceDate;
        }
    }
})
    .addMatcher(isAssetLocationChangeAction, (state, action) => {
    const [, requestedId] = action.payload.location.pathname.split("/").filter((e) => e);
    if (!state.currentDashboard.isManualChangeDate) {
        state.currentDashboard.referenceDate = state.entities.Asset[requestedId]?.referenceDate;
    }
})
    .addMatcher(isManagerLocationChangeAction, (state, action) => {
    const [, requestedId] = action.payload.location.pathname.split("/").filter((e) => e);
    if (!state.currentDashboard.isManualChangeDate) {
        state.datepicker.displayAs = DisplayAs.Date;
        if (state.entities.Manager[requestedId]?.latestCapitalAccountReportedDate ||
            state.entities.Manager[requestedId]?.referenceDate) {
            state.currentDashboard.referenceDate = state.entities.Manager[requestedId]?.latestCapitalAccountReportedDate ||
                state.entities.Manager[requestedId].referenceDate;
        }
    }
})
    .addMatcher(isMarketLocationChangeAction, (state, action) => {
    const [, requestedId] = action.payload.location.pathname.split("/").filter((e) => e);
    const obj = state.entities.Market?.[requestedId];
    if (!state.currentDashboard.isManualChangeDate && obj) {
        state.currentDashboard.referenceDate = obj.referenceDate;
    }
})
    .addMatcher(isFundLocationChangeAction, (state, action) => {
    const [, requestedId] = action.payload.location.pathname.split("/").filter((e) => e);
    const investorId = action.payload.location.search.split(INVESTOR_ID)?.[1] || state.currentDashboard.dashboardOptions?.selectedInvestorId;
    const lastInvestorCashFlowDate = state.entities?.Fund[requestedId]?.subscriptions?.find((e) => e.investmentId === investorId)?.lastInvestorCashFlowDate;
    const isMyPositionOn = state.currentDashboard?.dashboardOptions?.enabled;
    if (!state.currentDashboard.isManualChangeDate) {
        state.datepicker.displayAs = DisplayAs.Date;
        if (state.entities?.Fund[requestedId]?.latestCapitalAccountReportedDate && !lastInvestorCashFlowDate) {
            state.currentDashboard.referenceDate = state.entities.Fund[requestedId].latestCapitalAccountReportedDate;
        }
        else if (isMyPositionOn && lastInvestorCashFlowDate) {
            state.currentDashboard.referenceDate = lastInvestorCashFlowDate;
        }
        else if (state.entities.Fund[requestedId]?.referenceDate) {
            state.currentDashboard.referenceDate = state.entities.Fund[requestedId]?.referenceDate;
        }
    }
})
    .addMatcher(isFundSeriesLocationChangeAction, (state, action) => {
    const [, requestedId] = action.payload.location.pathname.split("/").filter((e) => e);
    if (!state.currentDashboard.isManualChangeDate) {
        state.datepicker.displayAs = DisplayAs.Date;
        if (state.entities.FundSeries[requestedId]?.referenceDate) {
            state.currentDashboard.referenceDate = state.entities.FundSeries[requestedId]?.referenceDate;
        }
    }
})
    .addMatcher(isLocationChangeAction, (state, action) => {
    if (isPortfolioLocationChangeAction(action) || isFundLocationChangeAction(action)) {
        AcwCopilotUtility.getInstance().updateVisibility(true);
    }
    else {
        AcwCopilotUtility.getInstance().updateVisibility(false);
    }
})
    .addMatcher(isMainGridLocationChangeAction, (state) => {
    state.mainGridSettings = initialState;
})
    .addMatcher(isCashFlowLocationChangeAction, (state, action) => {
    const isNew = action.payload.location.pathname.includes("custom-models/new");
    resetCashflowForecastingState(state.administration.cashflowForecastingState, isNew);
    if (!isNew) {
        state.currentDashboard.currentStatus.sections = {
            ...state.currentDashboard.currentStatus.sections,
            adminCFF: {
                selectedGroupedWidget: {},
                widgets: {
                    timeSeriesPreviewChart: {
                        inspectDataVisiblePeriods: 1
                    }
                }
            }
        };
    }
})
    .addMatcher(isPublicEquityIndicesLocationChangeAction, (state) => {
    if (state.grids.state[publicEquityIndicesGridState]?.selectedRows) {
        state.grids.state[publicEquityIndicesGridState].selectedRows = [];
    }
})
    .addMatcher(isPrivateBenchmarkLocationChangeAction, (state) => {
    if (state.grids.state[preqinBenchmarkStandardGrid]?.selectedRows) {
        state.grids.state[preqinBenchmarkStandardGrid].selectedRows = [];
    }
    if (state.grids.state[insightResearchStandardGrid]?.selectedRows) {
        state.grids.state[insightResearchStandardGrid].selectedRows = [];
    }
    if (state.grids.state[privateEquityIndicesCustomGrid]?.selectedRows) {
        state.grids.state[privateEquityIndicesCustomGrid].selectedRows = [];
    }
})
    .addMatcher(isCashflowModelAssociationChangeReduxQueryAction, (state) => {
    delete state.customQueries[modelsAssociationsQueryKey];
})
    .addMatcher(isPutModelAssociationReduxQueryAction, (state) => {
    resetDatapointsCache(state);
})
    .addMatcher(isDeletePortfoliosReduxQueryAction, (state, action) => {
    const portfolioIds = action?.body?.portfolioIds;
    Object.keys(state.entities.Datapoints).forEach((datapointRequest) => {
        if (state?.entities?.Datapoints?.[datapointRequest] &&
            state?.entities?.Datapoints?.[datapointRequest]?.responses &&
            state?.entities?.Datapoints?.[datapointRequest]?.responses?.[0]?.id === "CustomPortfolioGrid") {
            (state.entities.Datapoints?.[datapointRequest]?.responses?.[0]).series =
                (state.entities.Datapoints[datapointRequest]?.responses?.[0] ?? { series: [] })
                    .series?.filter((s) => !portfolioIds.includes(s.datapoints?.find((d) => (d.name ?? "") === "Id")?.value ?? ""));
        }
    });
})
    .addMatcher((action) => isFundResponseReduxQueryAction(action) || isFundLocationChangeAction(action), (state, action) => {
    const investorId = state.currentDashboard.dashboardOptions?.selectedInvestorId;
    let requestedId = "";
    if (action.payload?.location?.pathname) {
        [, requestedId] = action.payload.location.pathname.split("/").filter((e) => e);
    }
    else {
        requestedId = state.currentDashboard.currentEntityId;
    }
    const existSubscriptions = state?.currentDashboard?.dashboardOptions && state?.entities?.Fund[requestedId]?.subscriptions && investorId;
    if (existSubscriptions) {
        state.currentDashboard.dashboardOptions = {
            enabled: state.currentDashboard.dashboardOptions?.enabled ?? true,
            selectedInvestorId: investorId
        };
        const investorSubscription = state.entities.Fund[requestedId]?.subscriptions?.filter((x) => x.investmentId === investorId);
        if (investorSubscription && investorSubscription?.length > 0) {
            state.currentDashboard.currency = investorSubscription[0].currency;
        }
    }
    else if (!state.currentDashboard.dashboardOptions && state.entities.Fund[requestedId]?.subscriptions?.[0]?.currency) {
        state.currentDashboard.dashboardOptions = {
            enabled: false
        };
        state.currentDashboard.currency = state.entities.Fund[requestedId]?.subscriptions?.[0]?.currency;
    }
})
    .addMatcher(isActionType(setSelectedWidget), (state, action) => {
    if (action.payload.widgetId) {
        const widgetList = widgetAndChildrenArraySelector(action.payload.widgetId)(state);
        state.currentDashboard.currentStatus = {
            sections: {
                ...state.currentDashboard.currentStatus.sections,
                previewSection: initSectionStatus({
                    section: {
                        id: preview.sectionId
                    },
                    widgets: widgetList
                }, state.currentDashboard.referenceDate)
            }
        };
    }
})
    .addMatcher(isActionType(addWidgetToSection), (state, action) => {
    const currentSection = state.currentDashboard.currentStatus?.sections?.[action.payload.sectionId];
    if (action.payload.widget.id && currentSection) {
        const widgetList = widgetAndChildrenArraySelector(action.payload.widget.id)(state);
        const tempSection = initSectionStatus({ widgets: widgetList }, state.currentDashboard.referenceDate);
        const previewWidgetStatus = state.currentDashboard.currentStatus?.sections?.[preview.sectionId]?.widgets?.[preview.widgetId];
        if (previewWidgetStatus && tempSection.widgets?.[action.payload.widget.id]) {
            const widgetStatus = tempSection.widgets[action.payload.widget.id];
            widgetStatus.customDateRangeStartDate = previewWidgetStatus.customDateRangeStartDate;
            widgetStatus.customDateRangeEndDate = previewWidgetStatus.customDateRangeEndDate;
        }
        currentSection.widgets = { ...tempSection.widgets, ...currentSection.widgets };
        currentSection.selectedGroupedWidget = { ...tempSection.selectedGroupedWidget, ...currentSection.selectedGroupedWidget };
        setForceScrollToWidgetId(action.payload.widget.id);
    }
})
    .addMatcher(isAddWidgetCloseModalAction, (state) => {
    state.currentDashboard.currentStatus.sections && delete state.currentDashboard.currentStatus.sections.previewSection;
    state.addWidget = { widget: {} };
})
    .addMatcher(isDashboardChangedAction, (state) => {
    const { selectedSection: requestedSection, selectedType } = pathInformationSelector(state);
    const currentSection = state.designer.present.sections?.[requestedSection] || state.entities.Section?.[requestedSection];
    state.currentDashboard.currentSection = requestedSection;
    state.currentDashboard.currentAnalysis = findAnalysisIndex(state, requestedSection);
    if (currentSection) {
        populateSectionStatus(state, currentSection);
        state.currentDashboard.isMaximizedMode = false;
        if (state?.designer?.present?.active) {
            updateDesignerSectionsFromTemplate(state, selectedType);
        }
    }
})
    .addMatcher(analysesListFetched, (state, action) => {
    if (!action.payload) {
        return;
    }
    const entityKey = workspaceEntityTypeDataToWorkspaceEntityTypeMap(action.meta.arg.originalArgs.entityType);
    state.entities.Template[entityKey].type = entityKey;
    const templates = [...action.payload.analyses].sort((a, b) => (a.orderNumber ?? 0) - (b.orderNumber ?? 0));
    if (action.payload.isEditable === false) {
        // EFront workspace
        const hasLightPortfolioAnalytics = state.entities.UserSettings?.lightPortfolioAnalytics ?? false;
        state.entities.Template[entityKey] = getDefaultTemplate(hasLightPortfolioAnalytics)[entityKey];
        syncDashboardWithSection(state);
        return;
    }
    state.entities.Template[entityKey].analysis = [
        ...templates.map((x) => ({
            applicableAssetClass: x.applicableAssetClass,
            applicableAssetClasses: x.applicableAssetClasses,
            defaultFilters: x.defaultFilters,
            filters: x.filters,
            hideWhenMultipleAssetClasses: x.hideWhenMultipleAssetClasses,
            isDefault: x.isDefault,
            name: x.name,
            sections: x.sections?.map((section) => ({
                id: section.id,
                name: section.name,
                requiredModules: section.requiredModules
            }))
        }))
    ];
    state.entities.Section = { ...state.entities.Section,
        ...action.payload.analyses
            .flatMap((analysis) => analysis.sections)
            .reduce((acc, section) => {
            if (section?.id) {
                acc[section.id] = {
                    id: section.id,
                    isHorizontal: section.isHorizontal ?? false,
                    margin: section.margin,
                    name: section.name,
                    pageSize: section.pageSize,
                    printFirstPage: section.printFirstPage ?? false,
                    printLastPage: section.printLastPage ?? false,
                    requiredModules: section.requiredModules,
                    type: section.type,
                    widgets: {
                        all: section?.widgets?.all?.map((widget) => mapLayouts(widget)) ?? [],
                        myPositionsOnly: section?.widgets?.myPositionsOnly?.map((widget) => mapLayouts(widget)) ?? [],
                        wholeFundOnly: section?.widgets?.wholeFundOnly?.map((widget) => mapLayouts(widget)) ?? []
                    }
                };
            }
            return acc;
        }, {}) || {} };
    syncDashboardWithSection(state);
    state.currentDashboard.analysesIds = templates.map((x) => x.id);
    state.currentDashboard.currentAnalysisId = templates[state.currentDashboard.currentAnalysis ?? 0]?.id;
})
    .addMatcher(analysisUpdated, (state) => {
    const widgetsFromDesigner = state.designer.present.widgets;
    state.entities.Widget = {
        ...state.entities.Widget,
        ...widgetsFromDesigner
    };
})
    .addMatcher(pageFetched, (state, action) => {
    const { page } = action.payload;
    if (!page.id) {
        return;
    }
    action.payload.widgets.forEach((widget) => {
        state.entities.Widget = {
            ...state.entities.Widget,
            [widget.id]: {
                componentName: widget.componentName,
                componentProps: widget.componentProps,
                id: widget.id,
                metadata: widget.metadata,
                name: widget.name,
                requiredModules: widget.requiredModules
            }
        };
    });
    const currentSection = state.designer.present?.sections?.[page.id] || state.entities.Section?.[page.id];
    state.currentDashboard.currentSection = page.id;
    state.currentDashboard.currentAnalysis = findAnalysisIndex(state, page.id);
    state.currentDashboard.currentAnalysisId = state.currentDashboard.analysesIds?.[state.currentDashboard.currentAnalysis ?? 0];
    populateSectionStatus(state, currentSection);
})
    .addMatcher(isActionType(createInspectDataWidget), (state, action) => {
    if (action.payload) {
        const index = state.inspectData.inspectDataTab === InspectDataComparisonTab.ComparatePortfolio ||
            state.inspectData.inspectDataTab === InspectDataComparisonTab.MainPortfolio
            ? InspectDataTab.Portfolio
            : state.inspectData.inspectDataTab;
        const widgetList = state.inspectData.widgets[index];
        const previewWidget = state.currentDashboard.currentStatus.sections?.[preview.sectionId]?.widgets?.[preview.widgetId];
        const originalWidgetPipelineParameter = state.entities.Widget?.[action.payload.metadata.widgetId ?? ""]?.componentProps?.pipelineParameter;
        const userSettings = state.currentDashboard.currentStatus.sections?.[action.payload.metadata.sectionId || ""]?.widgets?.[action.payload.metadata.widgetId || ""];
        const datag = userSettings?.dataGranularity, showTotals = userSettings?.displayOptions?.showTotals;
        const hasDatePicker = (state.inspectData.inspectDataTab === InspectDataTab.Fund || state.inspectData.inspectDataTab === InspectDataTab.Asset) &&
            (!isEmpty(action.payload.columnDefs?.Fund) || !isEmpty(action.payload.columnDefs?.Asset));
        const previewSection = initSectionStatus({
            section: {
                id: preview.sectionId
            },
            widgets: [
                {
                    ...widgetList,
                    componentProps: {
                        userSettings: {
                            dataGranularity: datag,
                            dataScale: DataScale.unit,
                            dateSettingsDefaultValues: userSettings?.dateSettingsDefaultValues,
                            displayOptions: {
                                showTotals: showTotals ?? false
                            },
                            hasDatePicker,
                            periodOfAnalysis: {
                                defaultValue: userSettings?.periodOfAnalysis
                            }
                        }
                    },
                    id: inspectData.widgetId
                }
            ]
        });
        if (previewWidget && previewSection.widgets) {
            previewSection.widgets[preview.widgetId] = { ...previewWidget };
        }
        state.currentDashboard.currentStatus = {
            sections: {
                ...state.currentDashboard.currentStatus.sections,
                previewSection
            }
        };
        const pipelineParameter = widgetList.componentProps?.pipelineParameter || {};
        applyOriginalWidgetPipelineParameterVisitorsIntoInspectDataOne(pipelineParameter, originalWidgetPipelineParameter);
        state.entities.Widget.inspectDataWidget = {
            ...widgetList,
            componentProps: {
                ...widgetList.componentProps,
                userSettings: {
                    dataScale: DataScale.unit,
                    displayOptions: {
                        showTotals: showTotals ?? false
                    }
                }
            },
            id: inspectData.widgetId
        };
    }
})
    .addMatcher(isActionType(setCurrentStatusSectionWidget), (state, action) => {
    if (action.payload) {
        const compProps = state.entities.Widget[action.payload.widgetId]?.componentProps;
        const previewSection = initSectionStatus({
            section: {
                id: action.payload.sectionId
            },
            widgets: [
                {
                    componentProps: compProps,
                    id: action.payload.widgetId
                }
            ]
        }, state.currentDashboard.referenceDate);
        state.currentDashboard.currentStatus = {
            sections: {
                ...state.currentDashboard.currentStatus.sections,
                [action.payload.sectionId]: previewSection
            }
        };
    }
})
    .addMatcher(isActionType(setInspectDataSelectedTab), (state, action) => {
    if (action.payload) {
        const widgetList = action.payload === InspectDataComparisonTab.ComparatePortfolio || action.payload === InspectDataComparisonTab.MainPortfolio
            ? state.inspectData.widgets.ComparisonPortfolio
            : state.inspectData.widgets[action.payload];
        state.entities.Widget.inspectDataWidget = {
            ...widgetList,
            componentProps: {
                ...widgetList.componentProps,
                userSettings: {
                    dataScale: DataScale.unit,
                    displayOptions: {
                        showTotals: true
                    }
                }
            },
            id: inspectData.widgetId
        };
    }
})
    .addMatcher(isActionType(removeInspectDataWidget), (state) => {
    if (state.entities.Widget.inspectDataWidget) {
        state.entities.Widget.inspectDataWidget = {};
    }
})
    .addMatcher(isActionType(newWidgetReceived), (state, action) => {
    if (action.payload?.id) {
        state.entities.Widget[action.payload.id] = action.payload.newWidget;
        const widgetsAssistant = Object.keys(state.entities.Widget).filter((x) => x.startsWith(assistantChartPrefix));
        const allWidgets = [];
        widgetsAssistant.forEach((w) => {
            allWidgets.push(state.entities.Widget[w]);
        });
        const nextSection = {
            id: analyticsCopilotWidgetsSectionId,
            widgets: allWidgets
        };
        const section = initSectionStatus(nextSection, state.currentDashboard.referenceDate);
        state.currentDashboard.currentStatus = {
            sections: { ...state.currentDashboard.currentStatus.sections, [nextSection.id]: section }
        };
    }
})
    .addMatcher(isUpdateWidgetWorkshopColumnsAction, (state, action) => {
    const columnsOptions = state.workshop.editor.settings?.data?.columns?.options, context = capitalizeFirstLetter(state.router.location.pathname.split("/")?.[1]) || "", widgetComponentProps = state.workshop.editor.preview?.widget?.componentProps;
    const allowedDimensionsForCurrentContext = Object.values(state.entities.DatapointsAllowedDimensions)
        ?.find((e) => e.id === context)?.datapointAllowedDimensions ?? [];
    if (widgetComponentProps) {
        widgetComponentProps.defaultFilters = state?.workshop?.editor?.settings?.widgetFilterValues ?? {};
    }
    if (!state?.workshop?.editor || !columnsOptions || !widgetComponentProps) {
        return;
    }
    const isGridGroupingAction = [
        removeSettingsGroupingOptionsDatapoints.type,
        addSettingsGroupingOptionsDatapoints.type,
        addSettingsGroupingOptions.type,
        applyGroupingOnColumns.type,
        updateColumns.type,
        addColumn.type,
        removeColumns.type
    ].includes(action.type);
    if (state.workshop.header.widgetType === WidgetType.Grid && !isGridGroupingAction) {
        updateGridUserSettings(widgetComponentProps, columnsOptions);
    }
    if (state.workshop.editor.preview?.widget?.componentName !== "InsDatapointGridWidget" ||
        state.currentDashboard.currentType === DatapointsEntityType.Market.toLocaleLowerCase()) {
        return;
    }
    updateGridDimensions(widgetComponentProps, columnsOptions, allowedDimensionsForCurrentContext, action.type, state.workshop.editor.settings?.groupingOptionsDatapoints);
    syncPreviewGridColumns(state, columnsOptions);
})
    .addMatcher(isUpdatePreviewWidgetAction, (state) => {
    if (state.workshop.editor?.preview?.widget) {
        const existingPreviewWidget = state.currentDashboard.currentStatus.sections?.previewSection?.widgets?.[preview.widgetId];
        const existingDateSettings = existingPreviewWidget?.dateSettingsDefaultValues;
        const existingCustomDateRangeStartDate = existingPreviewWidget?.customDateRangeStartDate;
        const existingCustomDateRangeEndDate = existingPreviewWidget?.customDateRangeEndDate;
        const existingDefaultTenor = existingPreviewWidget?.defaultTenor;
        state.currentDashboard.currentStatus = {
            sections: {
                ...state.currentDashboard.currentStatus.sections,
                previewSection: initSectionStatus({
                    section: {
                        id: preview.sectionId
                    },
                    widgets: [{ ...state.workshop.editor.preview?.widget || {}, id: preview.widgetId }]
                }, state.currentDashboard.referenceDate)
            }
        };
        const previewWidget = state.currentDashboard.currentStatus.sections?.previewSection?.widgets?.[preview.widgetId];
        if (previewWidget) {
            if (existingDateSettings) {
                previewWidget.dateSettingsDefaultValues = existingDateSettings;
            }
            if (existingDefaultTenor) {
                previewWidget.defaultTenor = existingDefaultTenor;
            }
            previewWidget.customDateRangeStartDate = existingCustomDateRangeStartDate ?? previewWidget.customDateRangeStartDate;
            previewWidget.customDateRangeEndDate = existingCustomDateRangeEndDate ?? previewWidget.customDateRangeEndDate;
        }
        state.entities.Widget.previewWidget = state.workshop.editor.preview?.widget;
    }
})
    .addMatcher(isTaUndoRedoAction, (state) => {
    state.administration.cashflowForecastingState.taModelEditingState.undoRedoId = generate();
})
    .addMatcher(isModifyBenchmarkAllocationAction, (state, action) => {
    let allocations = state.benchmarkCreation.compositionRules.allocations || [];
    if (action.payload.selectedRows) {
        allocations = action.payload.selectedRows.reduce((accumulatedAllocations, allocationId) => {
            if (allocations.find((allocation) => allocation.marketId === allocationId)) {
                return accumulatedAllocations;
            }
            const market = state.entities.Markets[allocationId];
            return [
                ...accumulatedAllocations, {
                    marketId: allocationId,
                    marketName: market.name,
                    marketType: market.marketType,
                    percentWeight: 0
                }
            ];
        }, allocations);
    }
    if (action.payload.key) {
        allocations = allocations.map((allocation) => {
            if (allocation.marketId !== action.payload.key) {
                return allocation;
            }
            return {
                ...allocation,
                percentWeight: action.payload.value.allocation
            };
        });
    }
    if (action.payload.row) {
        allocations = allocations.filter((allocation) => allocation.marketId !== action.payload.row);
    }
    state.benchmarkCreation.compositionRules.allocations = allocations;
})
    .addMatcher(isTenorPreviewUpdatedAction, (state, action) => {
    if (state.workshop.editor.preview?.widget) {
        state.workshop.editor.preview.widget = {
            ...state.workshop.editor.preview.widget,
            componentProps: {
                ...state.workshop.editor.preview.widget.componentProps,
                defaultTenor: action.payload.selectedTenor
            }
        };
    }
})
    .addMatcher(isResetBenchmarkCreation, (state) => {
    delete state.grids.state[InsBenchmarkCreationBlendedGridId];
})
    .addMatcher(isActionType(lazyWidgetsLoaded), (state, action) => {
    state.entities.Widget = { ...state.entities.Widget, ...action.payload };
})
    .addMatcher(isActionType(lazySectionLoaded), (state, action) => {
    state.entities.Section[action.payload.sectionId] = action.payload.section;
})
    .addMatcher(isActionType(lazySectionWithWidgetsLoaded), (state, action) => {
    if (action.payload.section.id) {
        state.entities.Section[action.payload.section.id] = action.payload.section;
    }
    state.entities.Widget = { ...state.entities.Widget, ...action.payload.widgets };
    const { section } = action.payload;
    if (section.id) {
        populateSectionStatus(state, section);
    }
})
    .addMatcher(isActionType(resetWorkspaceSettings), (state, action) => {
    const eligibleWidgetIds = new Set(action.payload);
    const sections = state.currentDashboard.currentStatus?.sections;
    const referenceDate = state.currentDashboard.referenceDate ?? new Date();
    if (!sections) {
        return;
    }
    Object.values(sections).forEach((section) => {
        Object.entries(section?.widgets ?? {}).forEach(([widgetId, widget]) => {
            if (!widget || !eligibleWidgetIds.has(widgetId)) {
                return;
            }
            widget.isWidgetLevelTimePeriod = false;
            const entityWidget = state.entities.Widget[widgetId];
            const parentId = entityWidget?.parentId;
            const componentProps = parentId
                ? state.entities.Widget[parentId]?.componentProps
                : entityWidget?.componentProps;
            if (componentProps) {
                widget.dateSettingsDefaultValues = getDefaultValues(componentProps, referenceDate);
                widget.dataGranularity = componentProps.userSettings?.dataGranularity;
            }
        });
    });
}));
